/**
 * @file useGroupedExperimentTable.ts
 * @description This file contains the custom hook for all states and functions
 * used by a grouped experiment table
 */

import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import * as R from "ramda";
import { useSetError } from "../redux/modals";
import {
  GetFeatureExperimentsResponse,
  ExperimentFlag,
  SpecialUser,
  UserExperiment,
  CompanyExperiment,
} from "@blisspointmedia/bpm-types/dist/FeatureExperiments";
import { CompanyID, GetUserAdminResponse, User } from "@blisspointmedia/bpm-types/dist/UserAdmin";
import { awaitJSON, UsersLambdaFetch } from "../utils/fetch-utils";
import { SelectorOption } from "../Components/ModalEditTable";
import { makeRowId, getSpecialUsersByGroup, groupData, ungroupData } from "./utils";
import { GroupedDataRow } from "./types";

type UseGroupedExperimentTableReturn = any;

export const useGroupedExperimentTable = <
  T extends UserExperiment | CompanyExperiment | SpecialUser,
  K extends keyof T
>(
  jsonName: "user_experiments" | "company_experiments" | "special_users" | "experiment_flags",
  expectedKeys: (keyof T & string)[],
  groupByColumn: keyof T,
  groupedColumn: K,
  createNewItem: (groupKey: string, groupedItem: string) => T
): UseGroupedExperimentTableReturn => {
  type FrontendRow = GroupedDataRow<T, K>;

  // Data States
  const [tableData, setTableData] = useState<FrontendRow[]>();
  const [originalData, setOriginalData] = useState<Record<string, FrontendRow>>({});
  const [newExperiments, setNewExperiments] = useState<FrontendRow[]>([]);
  const [updatedExperiments, setUpdatedExperiments] = useState<FrontendRow[]>([]);
  const [deletedExperiments, setDeletedExperiments] = useState<FrontendRow[]>([]);

  // UI States
  const [deletedRows, setDeletedRows] = useState<FrontendRow[]>([]);
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userModalInputErrorMessage, setUserModalInputErrorMessage] = useState<string>("");

  // Use ref for loading state to avoid infinite loops
  const isLoadingDataRef = useRef(false);

  // Dropdown States
  const [allExperimentFlags, setAllExperimentFlags] = useState<ExperimentFlag[]>([]);
  const [userEmails, setUserEmails] = useState<string[]>([]);
  const [companies, setCompanies] = useState<string[]>([]);
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [specialUsers, setSpecialUsers] = useState<any[]>([]);

  // Set Error Message in UI
  const setError = useSetError();

  // Make sure the page is mounted to avoid leaks
  const isMountedRef = useRef(true);

  // Mapping from unique identifier to the original user experiment that came in from the API
  // Used to detect edited rows
  const [originalExperimentMap, setOriginalExperimentMap] = useState<
    Record<string, T & { id: string }>
  >({});

  // FUNCTIONS

  /**
   * Fetch all data needed from postgres tables
   *
   */
  const getFreshData = useCallback(async () => {
    // Prevent multiple concurrent calls
    if (isLoadingDataRef.current) {
      return;
    }

    isLoadingDataRef.current = true;
    setTableData(undefined);

    try {
      // Fetch all needed data from APIs
      const featureExperimentResponse = await UsersLambdaFetch("/getFeatureExperiments");
      const featureExperimentData: GetFeatureExperimentsResponse = await awaitJSON<GetFeatureExperimentsResponse>(
        featureExperimentResponse
      );

      // Ensure that the data that came in actually matches the type we are expecting
      const experimentsRaw = featureExperimentData[jsonName];
      if (!Array.isArray(experimentsRaw)) {
        throw new Error(
          `Expected an array for featureExperimentData[${String(
            jsonName
          )}], but got: ${typeof experimentsRaw}`
        );
      }

      if (experimentsRaw.length > 0) {
        for (let i = 0; i < experimentsRaw.length; i++) {
          const item = experimentsRaw[i];
          for (const key of expectedKeys) {
            if (!(key in item)) {
              throw new Error(
                `Item at index ${i} in featureExperimentData[${String(
                  jsonName
                )}] is missing expected key "${key}"`
              );
            }
          }
        }
      }
      const experiments: T[] = experimentsRaw as T[];

      const userAccessResponse = await UsersLambdaFetch("/userAdmin/getUsers");
      const userAccessData: GetUserAdminResponse = await awaitJSON<GetUserAdminResponse>(
        userAccessResponse
      );
      const allUsers: User[] = userAccessData.users;
      const allCompanies: CompanyID[] = userAccessData.companies;

      // Before doing anything, take the untouched experiment rows and create a mapping from
      // a unique id to each row. This will be used to detect row edits. We give that id to each
      // row so it can look up it's original later on.
      const originalDataMap: Record<string, T & { id: string }> = {};
      const rowsWithId: (T & { id: string })[] = experiments.map((row: T) => {
        const id = makeRowId([String(row[groupByColumn]), String(row[groupedColumn])]);
        const rowWithId = { ...row, id };
        originalDataMap[id] = rowWithId;
        return rowWithId;
      });

      // Group the user experiments by experiment_flag
      // user_email column gets aggregated, other columns come from most recent (lastmodified) row
      const groupedUserExperiments: FrontendRow[] = groupData<T, K>(
        rowsWithId,
        groupByColumn,
        groupedColumn
      );

      // Create a mapping from a unique ID to each grouped row
      // This is different then originalExperimentsMap, because this is grouped data.
      // This is used for the "Discard Changes" Button
      const originalGroupedDataMap: Record<string, FrontendRow> = {};
      if (groupedUserExperiments) {
        for (let experiment of groupedUserExperiments) {
          originalGroupedDataMap[experiment.id] = experiment;
        }
      }

      // Get the rest of the data needed for the selector options (dropdowns)
      const allExperimentFlags: ExperimentFlag[] = featureExperimentData.experiment_flags;

      const allUserEmails: string[] = allUsers.map(user => user.email);
      const allCompanyCids: string[] = allCompanies.map(company => company.cid);

      const allSpecialUsers: SpecialUser[] = featureExperimentData.special_users;
      const allUserGroups: string[] = Array.from(
        new Set(allSpecialUsers.map(specialUser => specialUser.user_group))
      );

      if (isMountedRef.current) {
        setOriginalData(originalGroupedDataMap);
        setTableData(groupedUserExperiments);
        setDeletedRows([]);
        setAllExperimentFlags(allExperimentFlags);
        setUserEmails(allUserEmails);
        setCompanies(allCompanyCids);
        setUserGroups(allUserGroups);
        setOriginalExperimentMap(originalDataMap);
        setSpecialUsers(allSpecialUsers);
      }
    } catch (e) {
      console.error("Error fetching User Experiments data:", e);
      const error: Error = e as Error;
      if (isMountedRef.current) {
        setError({
          message: `Error fetching User Experiments data: ${error.message}`,
          reportError: error,
        });
      }
    } finally {
      if (isMountedRef.current) {
        isLoadingDataRef.current = false;
      }
    }
  }, [jsonName, groupByColumn, groupedColumn, expectedKeys, setError]);

  // Save function
  const save = useCallback(async () => {
    try {
      // Step 1: Ungroup new and deleted experiments back into individual rows
      const newExperimentsDeaggregated: T[] = ungroupData<T, K>(
        newExperiments,
        groupByColumn,
        groupedColumn,
        originalExperimentMap,
        createNewItem
      );
      const deletedExperimentsDeaggregated: T[] = ungroupData<T, K>(
        deletedExperiments,
        groupByColumn,
        groupedColumn,
        originalExperimentMap,
        createNewItem
      );

      // Step 2: Figure out which rows need to be inserted and deleted for each updated experiment
      let editedInserts: T[] = [];
      let editedDeletes: T[] = [];

      updatedExperiments.forEach((row: FrontendRow) => {
        // Get an array of all values in the grouped column
        const currentValues: string[] = row[groupedColumn].map(String);

        // Get the original grouped row pre-edits
        const originalRow: FrontendRow = originalData[row.id];

        // Get the array of original values pre-edits
        const originalValues: string[] = originalRow[groupedColumn].map(String);

        let added: string[] = [];
        let removed: string[] = [];

        // If the groupByColumn changed, we need to delete all the original rows and inserts all the current rows
        // Otherwise, we only insert new values that weren't there before and delete values that were removed
        if (
          row[groupByColumn as keyof GroupedDataRow<T, K>] !==
          originalRow[groupByColumn as keyof GroupedDataRow<T, K>]
        ) {
          added = currentValues;
          removed = originalValues;
        } else {
          // If a valued wasn't there before, it needs it's own new row
          added = currentValues.filter(v => !originalValues.includes(v)) as string[];

          // If a value was removed, we need to delete the original row
          removed = originalValues.filter(v => !currentValues.includes(v)) as string[];
        }

        // Step 3: Create new individual rows for each inserted and deleted value
        added.forEach(value => {
          const experiment = createNewItem(
            String(row[groupByColumn as keyof GroupedDataRow<T, K>]),
            value
          );
          editedInserts.push(experiment);
        });

        removed.forEach(value => {
          // The key for the original rowshould be "groupByColumn|row[groupedColumn]"
          const key = makeRowId([String(row[groupByColumn as keyof GroupedDataRow<T, K>]), value]);
          const original: T = originalExperimentMap[key];
          let experiment = createNewItem(
            String(originalRow[groupByColumn as keyof GroupedDataRow<T, K>]),
            value
          );

          // Preserve original lastmodified and lastuser if they exist on the original
          if (original && "lastmodified" in original && "lastuser" in original) {
            experiment = {
              ...experiment,
              [groupByColumn]: original[groupByColumn],
              lastmodified: original.lastmodified,
              lastuser: original.lastuser,
            };
          }

          editedDeletes.push(experiment);
        });
      });

      // Step 4: Handle User Groups by transforming them into individual rows and handle duplicate rows
      const allInserts = [...newExperimentsDeaggregated, ...editedInserts].flatMap(
        (row: T): T[] => {
          const groupedColumnValue = String(row[groupedColumn]);
          if (groupedColumnValue.startsWith("group::")) {
            const userGroup = groupedColumnValue.slice("group::".length);
            const specialUsersByGroup = getSpecialUsersByGroup(userGroup, specialUsers).map(
              (user: SpecialUser) => {
                return {
                  ...row,
                  user_email: user.user,
                };
              }
            );

            return specialUsersByGroup.filter(specialUserRow => {
              const key = makeRowId([
                String(specialUserRow[groupByColumn]),
                String(specialUserRow[groupedColumn]),
              ]);
              if (
                originalExperimentMap[key] &&
                !editedDeletes.some(
                  r =>
                    r[groupByColumn] === specialUserRow[groupByColumn] &&
                    r[groupedColumn] === specialUserRow[groupedColumn]
                )
              ) {
                return false;
              }
              const existsInNew = newExperimentsDeaggregated.some(
                r =>
                  r[groupByColumn] === specialUserRow[groupByColumn] &&
                  r[groupedColumn] === specialUserRow[groupedColumn]
              );
              const existsInEdited = editedInserts.some(
                r =>
                  r[groupByColumn] === specialUserRow[groupByColumn] &&
                  r[groupedColumn] === specialUserRow[groupedColumn]
              );
              return !existsInNew && !existsInEdited;
            });
          }
          return [row];
        }
      );

      const allDeletes = [...deletedExperimentsDeaggregated, ...editedDeletes];

      // Step 5: Save the data to the database
      setSaving(true);

      try {
        await UsersLambdaFetch("/updateFeatureExperiments", {
          method: "POST",
          body: {
            [jsonName]: {
              insert: allInserts,
              delete: allDeletes,
            },
          },
        });

        if (isMountedRef.current) {
          await getFreshData();
        }

        setSaving(false);
      } catch (e) {
        console.error("Error during POST of feature experiment data:", e);
        const error: Error = e as Error;
        setSaving(false);
        setError({
          message: `Failed to POST feature experiment data: ${error.message}`,
          reportError: error,
        });
      }
    } catch (e) {
      if (isMountedRef.current) {
        const error: Error = e as Error;
        setSaving(false);
        let message = `Failed to set feature experiment data: ${error.message}`;
        setError({
          message: message,
          reportError: error,
        });
      }
    }
  }, [
    newExperiments,
    groupByColumn,
    groupedColumn,
    originalExperimentMap,
    createNewItem,
    deletedExperiments,
    updatedExperiments,
    jsonName,
    originalData,
    specialUsers,
    getFreshData,
    setError,
  ]);

  const clearAllChanges = useCallback(() => {
    if (isMountedRef.current) {
      setTableData(Object.values(originalData ?? {}));
      setDeletedRows([]);
      setNewExperiments([]);
      setDeletedExperiments([]);
    }
  }, [originalData, setTableData, setDeletedRows, setNewExperiments, setDeletedExperiments]);

  const hasPendingChanges: boolean = useMemo(() => {
    const hasPendingChangesResult =
      Boolean(newExperiments.length) ||
      Boolean(updatedExperiments.length) ||
      Boolean(deletedExperiments.length);
    if (!hasPendingChangesResult) {
      setShowPendingChanges(false);
    }
    return hasPendingChangesResult;
  }, [deletedExperiments, newExperiments, updatedExperiments, setShowPendingChanges]);

  const selectorOptions: Record<string, SelectorOption[]> = useMemo(() => {
    // Filter out experiment flags that are already in the table
    const existingExperimentFlags = new Set<string>();
    if (tableData) {
      tableData.forEach(row => {
        existingExperimentFlags.add(String(row[groupByColumn as keyof GroupedDataRow<T, K>]));
      });
    }

    const filteredExperimentFlags = allExperimentFlags.filter(
      flag => !existingExperimentFlags.has(flag.experiment_flag)
    );

    return {
      experiment_flag: filteredExperimentFlags.map(flag => ({
        label: flag.experiment_flag,
        value: flag.experiment_flag,
      })),
      user_email: [
        ...userGroups.map(group => ({
          label: `Group: ${group}`,
          value: `group::${group}`,
        })),
        ...userEmails.map(email => ({
          label: email,
          value: email,
        })),
      ],
      company: companies.map(company => ({
        label: company,
        value: company,
      })),
      special_user: userEmails
        .filter(email =>
          ["@blisspointmedia.com", "@tinuiti.com"].some(domain => email.endsWith(domain))
        )
        .map(email => ({
          label: email,
          value: email,
        })),
    };
  }, [allExperimentFlags, tableData, userEmails, userGroups, companies, groupByColumn]);

  // EFFECTS

  // Track component mount/unmount and reset everything
  useEffect(() => {
    isMountedRef.current = true; // Set on mount

    // Reset state and fetch data
    setTableData([]);
    setOriginalData({});
    setDeletedRows([]);
    setNewExperiments([]);
    setDeletedExperiments([]);
    setShowPendingChanges(false);
    setSaving(false);
    setUserModalInputErrorMessage("");
    setUserGroups([]);
    setAllExperimentFlags([]);
    isLoadingDataRef.current = false;
    getFreshData();

    // Cleanup function runs on unmount
    return () => {
      isMountedRef.current = false;
    };
  }, [getFreshData]);

  // When tableData changes, calculate new and deleted changes
  useEffect(() => {
    if (tableData && originalData) {
      // Find any experiments in tableData whose id isn't in originalData (new)
      const newNewExperiments = R.filter(row => {
        const { id } = row;
        return Boolean(!originalData[id]);
      }, tableData);
      setNewExperiments(newNewExperiments);

      // Find any experiments in deletedRows whose id is in originalData (deleted)
      const newDeletedExperiments = R.filter(row => {
        const { id } = row;
        return Boolean(originalData[id]);
      }, deletedRows);
      setDeletedExperiments(newDeletedExperiments);

      // Find any experiments in table data that have been edited
      const newUpdatedExperiments = R.filter(row => {
        const { id } = row;

        // Potential edited rows are any row in tableData that is in tableData,
        // but is not new and is not marked for deletion
        if (
          originalData[id] &&
          !R.find(row => row.id === id, deletedRows) &&
          !R.find(row => row.id === id, newNewExperiments)
        ) {
          // If you remove the extra metadata added by the table, does the row match
          // the row with the same id in the originalData? If not, this row was edited
          const omitIdIndexSelected = row => R.omit(["id", "index", "selected"], row);
          return !R.equals(omitIdIndexSelected(row), omitIdIndexSelected(originalData[id]));
        }
        return false;
      }, tableData);

      setUpdatedExperiments(newUpdatedExperiments);
    }
  }, [
    tableData,
    originalData,
    deletedRows,
    setNewExperiments,
    setDeletedExperiments,
    setUpdatedExperiments,
  ]);

  return {
    originalData,
    tableData,
    setTableData,
    newExperiments,
    updatedExperiments,
    deletedExperiments,
    hasPendingChanges,
    showPendingChanges,
    setShowPendingChanges,
    saving,
    save,
    clearAllChanges,
    deletedRows,
    setDeletedRows,
    userModalInputErrorMessage,
    setUserModalInputErrorMessage,
    selectorOptions,
    originalExperimentMap,
  };
};
